import React from 'react';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useLivePageModeFlag } from '@confluence/live-pages-features/entry-points/useLivePageModeFlag.ts';
import { useNoEditAccessFlag } from '@confluence/edit-features/entry-points/useNoEditAccessFlag.ts';
import { ShortcutListener } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';
import {
	LIVE_PAGE_MODE_SWITCH_TO_EDIT,
	LIVE_PAGE_MODE_SWITCH_TO_VIEW,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';

type ShortcutConfig = {
	shortcut: string;
	requiresEditPermissions?: boolean;
	requiredModeForShortcut: 'view' | 'edit';
	targetMode: 'view' | 'edit';
};

const shortcutConfigs: ShortcutConfig[] = [
	{
		requiredModeForShortcut: 'view',
		requiresEditPermissions: true,
		shortcut: LIVE_PAGE_MODE_SWITCH_TO_EDIT,
		targetMode: 'edit',
	},
	{
		requiredModeForShortcut: 'edit',
		shortcut: LIVE_PAGE_MODE_SWITCH_TO_VIEW,
		targetMode: 'view',
	},
];

export interface LivePageShortcutsProps {
	canEdit: boolean;
	flags: FlagsStateContainer;
}

export const LivePageShortcuts: React.ComponentType<Omit<LivePageShortcutsProps, 'flags'>> =
	withFlags(({ canEdit, flags }: LivePageShortcutsProps) => {
		const [{ mode }, { setMode }] = useLivePageMode();
		const { showLivePageModeFlag } = useLivePageModeFlag(flags);
		const { showNoEditAccessFlag } = useNoEditAccessFlag(flags);

		const handleShortcut = (
			nextMode: ShortcutConfig['targetMode'],
			requiresEditPermissions?: boolean,
		) => {
			const hasEditPermissions = !requiresEditPermissions || (requiresEditPermissions && canEdit);
			if (!hasEditPermissions) {
				showNoEditAccessFlag();
			} else {
				handleModeChange(nextMode);
			}
		};

		const handleModeChange = (nextMode: ShortcutConfig['targetMode']) => {
			if (mode !== nextMode) {
				try {
					showLivePageModeFlag(nextMode);
				} finally {
					void setMode(nextMode);
				}
			}
		};

		const applicableShortcuts = shortcutConfigs.filter(({ requiredModeForShortcut }) => {
			// Check if the shortcut is relevant for the current mode
			const isModeApplicable = mode === requiredModeForShortcut;
			return isModeApplicable;
		});

		if (applicableShortcuts.length === 0) return null;

		return (
			<>
				{applicableShortcuts.map(
					({ requiredModeForShortcut, shortcut, targetMode, requiresEditPermissions }) => (
						<ShortcutListener
							key={`${requiredModeForShortcut}-${shortcut}`}
							accelerator={shortcut}
							listener={() => handleShortcut(targetMode, requiresEditPermissions)}
						/>
					),
				)}
			</>
		);
	});
